import React, { useEffect, useState } from "react";
import {
  Button,
  DatePicker,
  Form,
  Input,
  Select,
  Switch,
  message,
  Upload,
  Tag,
} from "antd";
import { InboxOutlined, CloseOutlined } from "@ant-design/icons";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";

const { Option } = Select;
const { useForm } = Form;

const EditFundraiser = () => {
  const [documents, setDocuments] = useState([]);
  const [authorizedDocuments, setAuthorizedDocuments] = useState([]);
  const [campaignCovers, setCampaignCovers] = useState([]);
  const [deletedCampaignCovers, setDeletedCampaignCovers] = useState([]);
  const [fundraiserDetails, setFundraiserDetails] = useState(null);
  const [fetchStatus, setFetchStatus] = useState("idle");
  const [updateStatus, setUpdateStatus] = useState("idle");
  const [form] = useForm();
  const { fundraiserId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  useEffect(() => {
    const fetchFundraiserDetails = async (fundraiserId) => {
      setFetchStatus("loading");
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_API_URL_DASHBOARD_URL}/fundraiser/${fundraiserId}`
        );
        setFundraiserDetails(response.data);
        setFetchStatus("succeeded");
      } catch (error) {
        message.error("Failed to fetch fundraiser details.");
        setFetchStatus("failed");
      }
    };

    fetchFundraiserDetails(fundraiserId);
  }, [fundraiserId]);

  useEffect(() => {
    if (fundraiserDetails) {
      form.setFieldsValue({
        campaign: fundraiserDetails.campaign.name,
        campaignId: fundraiserDetails.campaign._id, // store campaign ID
        name: fundraiserDetails.name,
        goal: fundraiserDetails.goal.toString(),
        cause: fundraiserDetails.cause?.name,
        startDate: fundraiserDetails.dates.start_date
          ? moment(fundraiserDetails.dates.start_date)
          : null,
        endDate: fundraiserDetails.dates.last_date
          ? moment(fundraiserDetails.dates.last_date)
          : null,
        isActive: fundraiserDetails.status === "active",
        url: fundraiserDetails.slug,
        impacts: fundraiserDetails.impact,
      });
      setCampaignCovers(
        fundraiserDetails.cover.map((url, index) => ({
          url,
          uid: `cover-${index}`,
        }))
      );
      setAuthorizedDocuments(
        fundraiserDetails.documents?.map((url, index) => ({
          url,
          uid: `auth-doc-${index}`,
        })) || []
      );
    }
  }, [fundraiserDetails, form]);

  const uploadDocument = async (
    file,
    setLoading,
    setUploadSuccess,
    setMessage,
    setKey
  ) => {
    setLoading(true);
    const token = cookies.get("jwtToken");
    const filename = file.name;

    try {
      // Step 1: Get the pre-signed S3 bucket URL
      const uploadResponse = await axios.post(
        `${import.meta.env.VITE_API_URL}/upload/private_documents`,
        { filename },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const { url, key } = uploadResponse.data; // Capture the 'key' here

      // Step 2: Upload the file to the S3 bucket using fetch
      const s3UploadResponse = await fetch(url, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      if (s3UploadResponse.ok) {
        message.success("Document uploaded successfully!");
        setUploadSuccess(true);
        setMessage(filename); // Use filename as the message
        setKey(key); // Set the key for campaignData
      } else {
        throw new Error("Failed to upload file to S3");
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      message.error("An error occurred while uploading the document.");
      setUploadSuccess(false);
    }
    setLoading(false);
  };

  const handleDocumentUpload = async ({ file, onSuccess, onError }) => {
    setLoading(true);
    uploadDocument(
      file,
      setLoading,
      (success) => {
        if (success) {
          setDocuments((prevDocs) => [
            ...prevDocs,
            {
              url: `${import.meta.env.VITE_S3_BASE_URL}/${file.name}`,
              uid: file.uid,
            },
          ]);
          onSuccess(null, file);
        } else {
          onError(new Error("Failed to upload document"));
        }
      },
      (filename) => message.success(`${filename} uploaded successfully!`),
      (key) => setDocuments((prevDocs) => [...prevDocs, key]) // Store the key
    );
  };

const handleAuthorizedDocumentUpload = async ({ file, onSuccess, onError }) => {
    setLoading(true);
    uploadDocument(
        file,
        setLoading,
        (success) => {
            if (success) {
                setAuthorizedDocuments((prevDocs) => [
                    ...prevDocs,
                    { url: `${import.meta.env.VITE_S3_BASE_URL}/${file.name}`, uid: file.uid },
                ]);
                onSuccess(null, file);
            } else {
                onError(new Error("Failed to upload document"));
            }
        },
        (filename) => message.success(`${filename} uploaded successfully!`)
    );
};

  const handleRemoveImage = (uid) => {
    setDocuments((prevDocs) => prevDocs.filter((doc) => doc.uid !== uid));
  };

  const handleRemoveAuthorizedImage = (uid) => {
    setAuthorizedDocuments((prevDocs) =>
      prevDocs.filter((doc) => doc.uid !== uid)
    );
  };

  const handleRemoveCampaignCover = (uid) => {
    const cover = campaignCovers.find((cover) => cover.uid === uid);
    if (cover) {
      setCampaignCovers((prevCovers) =>
        prevCovers.filter((cover) => cover.uid !== uid)
      );
      setDeletedCampaignCovers((prevDeleted) => [...prevDeleted, cover.url]);
    }
  };

  const onFinish = async (values) => {
    const goal = parseFloat(values.goal);
    const fundraiserData = {
      name: values.name,
      goal: goal,
      campaign: values.campaignId, // send campaign._id directly
      dates: {
        start_date: values.startDate
          ? values.startDate.toISOString()
          : moment().toISOString(),
        last_date: values.endDate ? values.endDate.toISOString() : null,
      },
      status: values.isActive ? "active" : "draft",
      cover: [
        ...campaignCovers
          .filter((cover) => !deletedCampaignCovers.includes(cover.url))
          .map((cover) => cover.url),
        ...documents.map((doc) => doc.url),
      ],
      slug: values.url,
      impacts: [],
    };

    setUpdateStatus("loading");
    try {
      await axios.patch(
        `${import.meta.env.VITE_API_URL_DASHBOARD_URL}/fundraiser/${fundraiserId}`,
        fundraiserData
      );
      message.success("Fundraiser updated successfully!");
      navigate("/dashboard");
    } catch (error) {
      message.error(error.message || "Failed to update fundraiser.");
    } finally {
      setUpdateStatus("idle");
    }
  };

  return (
    <div className="bg-[#93c5fd] pt-10 ml-36 min-h-screen">
      <div className="max-w-4xl mx-auto bg-white p-8 rounded-lg shadow">
        {fetchStatus === "loading" ? (
          <div>Loading...</div>
        ) : (
          <Form
            form={form}
            onFinish={onFinish}
            layout="vertical"
            className="mt-6"
          >
            <Form.Item
              label="Choose Campaign"
              name="campaign"
              rules={[{ required: true, message: "Please select a campaign" }]}
            >
              <Select placeholder="Select a campaign" disabled>
                {fundraiserDetails && (
                  <Option value={fundraiserDetails.campaign._id}>
                    {fundraiserDetails.campaign.name}
                  </Option>
                )}
              </Select>
            </Form.Item>
            
            <Form.Item name="campaignId" hidden>
              <Input />
            </Form.Item>

            <Upload.Dragger
              accept="image/*"
              customRequest={handleDocumentUpload}
              showUploadList={false}
              multiple
              className="mt-4"
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag file to this area to upload
              </p>
            </Upload.Dragger>

            <div className="image-preview-grid mt-4 bg-gray-100 mb-4 rounded-lg min-h-0">
              {campaignCovers.map((cover) => (
                <div key={cover.uid} className="relative w-40 h-40 mb-2 mt-2">
                  <img
                    src={cover.url}
                    alt="Campaign Cover"
                    className="w-full h-full object-cover rounded-lg"
                  />
                  <Button
                    type="text"
                    className="absolute top-1 right-1 text-white bg-gray-800 rounded-full p-1"
                    onClick={() => handleRemoveCampaignCover(cover.uid)}
                    icon={<CloseOutlined />}
                  />
                </div>
              ))}
            </div>

            <Upload.Dragger
              accept="image/*"
              customRequest={handleAuthorizedDocumentUpload}
              showUploadList={false}
              multiple
              className="mt-4"
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag file to this area to upload
              </p>
            </Upload.Dragger>

            <Form.Item
              label="Fundraiser Name"
              name="name"
              rules={[
                { required: true, message: "Please input the fundraiser name" },
              ]}
            >
              <Input placeholder="Enter fundraiser name" />
            </Form.Item>
            <Form.Item
              label="URL"
              name="url"
              rules={[{ required: true, message: "Please input the url" }]}
            >
              <Input
                placeholder="Enter the URL of the fundraiser"
                className="input-style"
              />
            </Form.Item>

            <Form.Item
              label="Fundraiser Goal (PKR)"
              name="goal"
              rules={[
                { required: true, message: "Please input the fundraiser goal" },
              ]}
            >
              <Input
                placeholder="Enter fundraiser goal"
                onKeyPress={(event) => {
                  if (!/[0-9]/.test(event.key)) {
                    event.preventDefault();
                  }
                }}
              />
            </Form.Item>

            <Form.Item
              label="Cause"
              name="cause"
              rules={[
                {
                  required: true,
                  message: "Please input the cause for fundraising",
                },
              ]}
            >
              <Input placeholder="Enter cause for fundraising" />
            </Form.Item>

            <Form.Item
              label="Start Date"
              name="startDate"
              rules={[
                { required: true, message: "Please select the start date" },
              ]}
            >
              <DatePicker className="w-full" />
            </Form.Item>

            <Form.Item label="End Date" name="endDate">
              <DatePicker className="w-full" />
            </Form.Item>

            <Form.Item
              label="Is Active"
              name="isActive"
              valuePropName="checked"
              className="mb-4"
            >
              <Switch />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={updateStatus === "loading"}
              >
                Update Fundraiser
              </Button>
            </Form.Item>
          </Form>
        )}
      </div>
    </div>
  );
};

export default EditFundraiser;
